/**
 * Dashboard Component - Main dashboard showing Gateway status and statistics
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { gatewayStore, type GatewayState } from "../stores/gateway-store.js";

@customElement("piagnet-dashboard")
export class Dashboard extends LitElement {
	@state() private state: GatewayState = gatewayStore.state;
	@state() private currentTime = new Date();

	private unsubscribe?: () => void;
	private timeInterval?: ReturnType<typeof setInterval>;

	static styles = css`
		:host {
			display: block;
			padding: 1.5rem;
		}

		.header {
			display: flex;
			justify-content: space-between;
			align-items: center;
			margin-bottom: 1.5rem;
		}

		.title {
			font-size: 1.5rem;
			font-weight: 600;
			color: var(--text-primary, #111827);
		}

		.status-badge {
			display: inline-flex;
			align-items: center;
			gap: 0.5rem;
			padding: 0.5rem 1rem;
			border-radius: 9999px;
			font-size: 0.875rem;
			font-weight: 500;
		}

		.status-badge.online {
			background: #dcfce7;
			color: #166534;
		}

		.status-badge.offline {
			background: #fee2e2;
			color: #991b1b;
		}

		.status-dot {
			width: 8px;
			height: 8px;
			border-radius: 50%;
			background: currentColor;
		}

		.status-badge.online .status-dot {
			animation: pulse 2s infinite;
		}

		@keyframes pulse {
			0%, 100% { opacity: 1; }
			50% { opacity: 0.5; }
		}

		.stats-grid {
			display: grid;
			grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
			gap: 1rem;
			margin-bottom: 1.5rem;
		}

		.stat-card {
			background: white;
			border: 1px solid var(--border-color, #e5e7eb);
			border-radius: 0.75rem;
			padding: 1.25rem;
			box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
		}

		.stat-label {
			font-size: 0.875rem;
			color: var(--text-secondary, #6b7280);
			margin-bottom: 0.5rem;
		}

		.stat-value {
			font-size: 1.875rem;
			font-weight: 600;
			color: var(--text-primary, #111827);
		}

		.stat-subtext {
			font-size: 0.75rem;
			color: var(--text-tertiary, #9ca3af);
			margin-top: 0.25rem;
		}

		.section {
			background: white;
			border: 1px solid var(--border-color, #e5e7eb);
			border-radius: 0.75rem;
			padding: 1.25rem;
			margin-bottom: 1rem;
			box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
		}

		.section-title {
			font-size: 1.125rem;
			font-weight: 600;
			color: var(--text-primary, #111827);
			margin-bottom: 1rem;
		}

		.channel-list {
			display: flex;
			flex-direction: column;
			gap: 0.75rem;
		}

		.channel-item {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 0.75rem;
			background: var(--bg-secondary, #f9fafb);
			border-radius: 0.5rem;
		}

		.channel-info {
			display: flex;
			align-items: center;
			gap: 0.75rem;
		}

		.channel-icon {
			width: 2rem;
			height: 2rem;
			border-radius: 0.375rem;
			background: var(--brand-color, #3b82f6);
			color: white;
			display: flex;
			align-items: center;
			justify-content: center;
			font-weight: 600;
			font-size: 0.75rem;
		}

		.channel-name {
			font-weight: 500;
			color: var(--text-primary, #111827);
		}

		.channel-status {
			font-size: 0.75rem;
			color: var(--text-secondary, #6b7280);
		}

		.channel-actions {
			display: flex;
			gap: 0.5rem;
		}

		.btn {
			padding: 0.375rem 0.75rem;
			border-radius: 0.375rem;
			font-size: 0.875rem;
			font-weight: 500;
			cursor: pointer;
			border: none;
			transition: all 0.2s;
		}

		.btn-sm {
			padding: 0.25rem 0.5rem;
			font-size: 0.75rem;
		}

		.btn-primary {
			background: var(--brand-color, #3b82f6);
			color: white;
		}

		.btn-primary:hover {
			background: var(--brand-color-dark, #2563eb);
		}

		.btn-secondary {
			background: var(--bg-secondary, #f3f4f6);
			color: var(--text-primary, #374151);
		}

		.btn-secondary:hover {
			background: var(--bg-tertiary, #e5e7eb);
		}

		.btn-danger {
			background: #fee2e2;
			color: #991b1b;
		}

		.btn-danger:hover {
			background: #fecaca;
		}

		.session-list {
			display: flex;
			flex-direction: column;
			gap: 0.5rem;
		}

		.session-item {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 0.75rem;
			background: var(--bg-secondary, #f9fafb);
			border-radius: 0.5rem;
			font-size: 0.875rem;
		}

		.session-key {
			font-family: monospace;
			color: var(--text-secondary, #6b7280);
		}

		.session-state {
			padding: 0.125rem 0.5rem;
			border-radius: 9999px;
			font-size: 0.75rem;
			font-weight: 500;
		}

		.session-state.active {
			background: #dcfce7;
			color: #166534;
		}

		.session-state.idle {
			background: #f3f4f6;
			color: #6b7280;
		}

		.empty-state {
			text-align: center;
			padding: 2rem;
			color: var(--text-secondary, #6b7280);
		}

		.quick-actions {
			display: flex;
			gap: 0.75rem;
			flex-wrap: wrap;
		}
	`;

	connectedCallback() {
		super.connectedCallback();
		this.unsubscribe = gatewayStore.subscribe((state) => {
			this.state = state;
		});
		this.timeInterval = setInterval(() => {
			this.currentTime = new Date();
		}, 1000);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.unsubscribe?.();
		if (this.timeInterval) {
			clearInterval(this.timeInterval);
		}
	}

	render() {
		const status = this.state.status;

		return html`
			<div class="header">
				<h1 class="title">Dashboard</h1>
				<div class="status-badge ${this.state.connected ? "online" : "offline"}">
					<span class="status-dot"></span>
					${this.state.connected ? "Connected" : "Disconnected"}
				</div>
			</div>

			<div class="stats-grid">
				<div class="stat-card">
					<div class="stat-label">Active Sessions</div>
					<div class="stat-value">${status?.sessions ?? "-"}</div>
					<div class="stat-subtext">${this.state.sessions.filter((s) => s.state === "active").length} active now</div>
				</div>

				<div class="stat-card">
					<div class="stat-label">WebSocket Connections</div>
					<div class="stat-value">${status?.connections ?? "-"}</div>
					<div class="stat-subtext">Gateway port ${status?.port ?? 18789}</div>
				</div>

				<div class="stat-card">
					<div class="stat-label">Connected Channels</div>
					<div class="stat-value">${this.state.channels.filter((c) => c.connected).length}</div>
					<div class="stat-subtext">of ${this.state.channels.length} configured</div>
				</div>

				<div class="stat-card">
					<div class="stat-label">Uptime</div>
					<div class="stat-value">${this.formatUptime(status?.startTime)}</div>
					<div class="stat-subtext">${status?.startTime ? this.formatTime(status.startTime) : "Not started"}</div>
				</div>
			</div>

			<div class="section">
				<h2 class="section-title">Channels</h2>
				${this.state.channels.length === 0
					? html`
							<div class="empty-state">No channels configured</div>
						`
					: html`
							<div class="channel-list">
								${this.state.channels.map(
									(channel) => html`
										<div class="channel-item">
											<div class="channel-info">
												<div class="channel-icon">${channel.type[0].toUpperCase()}</div>
												<div>
													<div class="channel-name">${channel.name}</div>
													<div class="channel-status">
														${channel.enabled ? (channel.connected ? "🟢 Connected" : "🔴 Disconnected") : "⚪ Disabled"}
													</div>
												</div>
											</div>
											<div class="channel-actions">
												<button
													class="btn btn-sm btn-secondary"
													@click=${() => this.restartChannel(channel.type)}
													disabled=${!channel.enabled}
												>
													Restart
												</button>
											</div>
										</div>
									`,
								)}
							</div>
						`}
			</div>

			<div class="section">
				<h2 class="section-title">Active Sessions</h2>
				${this.state.sessions.length === 0
					? html`
							<div class="empty-state">No active sessions</div>
						`
					: html`
							<div class="session-list">
								${this.state.sessions.slice(0, 10).map(
									(session) => html`
										<div class="session-item">
											<span class="session-key">${this.formatSessionKey(session.key)}</span>
											<span class="session-state ${session.state}">${session.state}</span>
										</div>
									`,
								)}
							</div>
						`}
			</div>

			<div class="section">
				<h2 class="section-title">Quick Actions</h2>
				<div class="quick-actions">
					<button class="btn btn-primary" @click=${this.startGateway} ?disabled=${this.state.connected}>
						Start Gateway
					</button>
					<button class="btn btn-danger" @click=${this.stopGateway} ?disabled=${!this.state.connected}>
						Stop Gateway
					</button>
				</div>
			</div>
		`;
	}

	private formatSessionKey(key: string): string {
		// agent:{agentId}:{channel}:{scope}:{identifier}
		const parts = key.split(":");
		if (parts.length >= 5) {
			return `${parts[2]}:${parts[3]}:${parts[4].substring(0, 20)}${parts[4].length > 20 ? "..." : ""}`;
		}
		return key;
	}

	private formatUptime(startTime?: Date): string {
		if (!startTime) return "-";
		const diff = this.currentTime.getTime() - new Date(startTime).getTime();
		const hours = Math.floor(diff / (1000 * 60 * 60));
		const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
		return `${hours}h ${minutes}m`;
	}

	private formatTime(date: Date): string {
		return new Date(date).toLocaleString();
	}

	private startGateway() {
		gatewayStore.startGateway();
	}

	private stopGateway() {
		gatewayStore.stopGateway();
	}

	private restartChannel(channelType: string) {
		gatewayStore.restartChannel(channelType);
	}
}

// Global styles
const style = document.createElement("style");
style.textContent = `
	:root {
		--brand-color: #3b82f6;
		--brand-color-dark: #2563eb;
		--text-primary: #111827;
		--text-secondary: #6b7280;
		--text-tertiary: #9ca3af;
		--border-color: #e5e7eb;
		--bg-secondary: #f9fafb;
		--bg-tertiary: #f3f4f6;
	}
`;
document.head.appendChild(style);
